import { Link, useNavigate, ClientOnly } from "@tanstack/react-router";
import { Settings as SettingsIcon, Bell, BarChart3, LogOut } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePremium } from "@/hooks/usePremium";
import { supabase } from "@/integrations/supabase/client";
import { useT } from "@/i18n/locale-context";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

function AuthSlotFallback() {
  return (
    <div className="flex items-center gap-3" aria-hidden>
      <div className="h-9 w-28 rounded-full bg-foreground/5" />
    </div>
  );
}

const ADMIN_EMAILS = new Set(["oleg.rezabek@gmail.com"]);

function AuthSlot() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const t = useT();

  async function signOut() {
    await supabase.auth.signOut();
    nav({ to: "/" });
  }

  if (loading) return <AuthSlotFallback />;

  const isAdmin = !!user?.email && ADMIN_EMAILS.has(user.email.toLowerCase());
  const iconBtn =
    "size-9 rounded-full grid place-items-center bg-card border border-foreground/10 text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors";
  const iconBtnActive =
    "size-9 rounded-full grid place-items-center bg-foreground text-background border border-foreground";

  return user ? (
    <div className="flex items-center gap-2">
      <Link to="/app" className="text-sm font-medium hover:text-foreground hidden md:inline">{t("header.nav.my_analysis")}</Link>
      <Link to="/protocols" className="text-sm font-medium hover:text-foreground hidden md:inline">{t("header.nav.protocols")}</Link>
      <Link to="/progress" className="text-sm font-medium hover:text-foreground hidden md:inline">{t("header.nav.progress")}</Link>
      <Link to="/history" className="text-sm font-medium hover:text-foreground hidden md:inline">{t("header.nav.history")}</Link>
      <Link to="/account" className="text-sm font-medium hover:text-foreground hidden md:inline mr-1">{t("header.nav.account")}</Link>
      <Link
        to="/settings"
        hash="notifications"
        title="Notifications"
        aria-label="Notifications"
        className={iconBtn}
        activeProps={{ className: iconBtnActive }}
      >
        <Bell size={16} />
      </Link>
      {isAdmin && (
        <Link
          to="/conso"
          title="Consommation (admin)"
          aria-label="Consommation"
          className={iconBtn}
          activeProps={{ className: iconBtnActive }}
        >
          <BarChart3 size={16} />
        </Link>
      )}
      <Link
        to="/settings"
        title={t("header.nav.settings") === "header.nav.settings" ? "Settings" : t("header.nav.settings")}
        aria-label="Settings"
        className={iconBtn}
        activeProps={{ className: iconBtnActive }}
      >
        <SettingsIcon size={16} />
      </Link>
      <button
        onClick={signOut}
        title={t("header.nav.sign_out")}
        aria-label={t("header.nav.sign_out")}
        className={iconBtn}
      >
        <LogOut size={16} />
      </button>
    </div>
  ) : (
    <Link
      to="/login"
      className="bg-foreground text-background px-6 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
    >
      {t("header.cta.sign_in")}
    </Link>
  );
}

export function Header() {
  const t = useT();
  const { isPremium } = usePremium();
  return (
    <nav className="border-b border-foreground/5 bg-background/80 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="size-8 bg-accent rounded-lg flex items-center justify-center">
            <div className="size-3 bg-accent-foreground rounded-full" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight uppercase">{t("header.brand")}</span>
        </Link>
        <div className="hidden md:flex gap-8 text-sm font-medium text-muted-foreground">
          <Link to="/" hash="methode" className="hover:text-foreground">{t("header.nav.method")}</Link>
          <Link to="/billing" className="hover:text-foreground">
            {isPremium ? t("header.nav.billing") : t("header.nav.premium")}
          </Link>
          <Link to="/" hash="privacy" className="hover:text-foreground">{t("header.nav.privacy")}</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <ClientOnly fallback={<AuthSlotFallback />}>
            <AuthSlot />
          </ClientOnly>
        </div>
      </div>
    </nav>
  );
}
